/**
 * Faz 4-20: WebAuthn (FIDO2) helpers.
 *
 * Backend Fido2NetLib options ham JSON döner; tarayıcının
 * `navigator.credentials.create/get` API'leri ArrayBuffer bekler.
 * Bu modül base64url ↔ Uint8Array dönüşümü yapan ince bir köprü.
 *
 * RP ID + origin: backend `WEBAUTHN_RP_ID` env'ine bağlı (default
 * "localhost"). Web sayfası bu RP ID ile aynı domain altında olmalı,
 * aksi halde tarayıcı `SecurityError` fırlatır. Dev'de
 * `vite preview` veya backend reverse-proxy ile aynı origin.
 */
function base64UrlToUint8(s) {
    const pad = '='.repeat((4 - (s.length % 4)) % 4);
    const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
function uint8ToBase64Url(b) {
    const u8 = b instanceof Uint8Array ? b : new Uint8Array(b);
    let s = '';
    for (let i = 0; i < u8.length; i++)
        s += String.fromCharCode(u8[i]);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/** Backend CredentialCreateOptions JSON → browser PublicKeyCredentialCreationOptions */
export function decodeCreateOptions(opts) {
    return {
        ...opts,
        challenge: base64UrlToUint8(opts.challenge),
        user: {
            ...opts.user,
            id: base64UrlToUint8(opts.user.id),
        },
        excludeCredentials: (opts.excludeCredentials ?? []).map((c) => ({
            ...c,
            id: base64UrlToUint8(c.id),
        })),
    };
}
/** Browser AuthenticatorAttestationResponse → backend AuthenticatorAttestationRawResponse JSON */
export function encodeAttestationResponse(cred) {
    const att = cred.response;
    return {
        id: cred.id,
        rawId: uint8ToBase64Url(cred.rawId),
        type: cred.type,
        response: {
            attestationObject: uint8ToBase64Url(att.attestationObject),
            clientDataJSON: uint8ToBase64Url(att.clientDataJSON),
        },
        extensions: cred.getClientExtensionResults?.() ?? {},
    };
}
/** Backend AssertionOptions JSON → browser PublicKeyCredentialRequestOptions */
export function decodeAssertionOptions(opts) {
    return {
        ...opts,
        challenge: base64UrlToUint8(opts.challenge),
        allowCredentials: (opts.allowCredentials ?? []).map((c) => ({
            ...c,
            id: base64UrlToUint8(c.id),
        })),
    };
}
/** Browser AuthenticatorAssertionResponse → backend AuthenticatorAssertionRawResponse JSON */
export function encodeAssertionResponse(cred) {
    const ass = cred.response;
    return {
        id: cred.id,
        rawId: uint8ToBase64Url(cred.rawId),
        type: cred.type,
        response: {
            authenticatorData: uint8ToBase64Url(ass.authenticatorData),
            clientDataJSON: uint8ToBase64Url(ass.clientDataJSON),
            signature: uint8ToBase64Url(ass.signature),
            userHandle: ass.userHandle ? uint8ToBase64Url(ass.userHandle) : null,
        },
        extensions: cred.getClientExtensionResults?.() ?? {},
    };
}
/** Tarayıcı WebAuthn destekliyor mu, platform authenticator (Hello) var mı. */
export async function detectWebAuthnSupport() {
    if (typeof PublicKeyCredential === 'undefined') {
        return { supported: false, platformAvailable: false };
    }
    let platform = false;
    try {
        platform = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    }
    catch {
        platform = false;
    }
    return { supported: true, platformAvailable: platform };
}
